<script>
  import { poll } from 'nandos-timing-utils'
  import PrefersReducedMotion from '../../utils/prefers-reduced-motion'

  export default {

    props: {
      perPage: { type: Number, default: 1 },
      autoAdvanceTime: { type: Number, default: 0 },
      withBreadcrumbs: { type: Boolean, default: true },
      withControlBtns: { type: Boolean, default: false },
      overlayBreadcrumbs: { type: Boolean, default: false },
      overlayControlBtns: { type: Boolean, default: false },

      peek: { type: Boolean, default: false },
      peekAmount: { type: Number, default: 0.15 },
      centerPeakSlides: { type: Boolean, default: true },

      reduceUnfilledPageSize: { type: Boolean, default: true },
      scrollbehavior: { type: String, default: PrefersReducedMotion ? 'auto' : 'smooth' },
    },

    data() {
      return {
        currentPage: 0,
        _supportsIntersectionObserver: "IntersectionObserver" in window,
        _observer: null,
        _poller: null,
      }
    },

    computed: {
      rawSlides() {
        return this.$slots.default
      },

      numSlides() {
        return this.rawSlides && this.rawSlides.length || 0
      },

      numPages() {
        return Math.ceil(this.numSlides / this.perPage)
      },

      /**
       * When the last page contains less slides than `perPage`, adjust the last page's width so it takes up
       * the appropriate amount of space proportional to the amount of slides it actually contains.
       * Eg, if we have 5 slides, and are displaying 2 `perPage`, the last page would only contain 1 slide
       * so it should only be 50% wide --> page columns == [100% 100% 50%]
       */
      isReducedLastPage() {
        return this.reduceUnfilledPageSize && (this.numSlides % this.perPage != 0)
      },
    },

    watch: {
      currentPage() {
        this.$emit('page-changed', this.currentPage)
      }
    },

    render(h) {
      if(this.numSlides == 0) return

      return h(
        'section',
        {
          class: {
            'carousel': true,
            'carousel--overlay-breadcrumbs': this.overlayBreadcrumbs,
            'carousel--overlay-control-btns': this.overlayControlBtns
          },
          on: {
            // Stop auto-advance if we receive user interaction
            'mouseenter': this.pausePoller,
            'mouseleave': this.resumePoller,
            'touchstart': this.pausePoller,
            'touchend': this.resumePoller,
            /*capture.once*/'~!keydown': this.destroyPoller,
          },
        },
        [
          h('div', {class:'carousel__wrapper'}, [
            this.withControlBtns ? this.genPageControlBtn(h, 'backward') : null,
            this.genScroller(h),
            this.withControlBtns ? this.genPageControlBtn(h, 'forward') : null
          ]),
          this.withBreadcrumbs ? this.genPageBreadcrumbs(h) : null,
        ]
      ) 
    },

    mounted() {
      if(this.numSlides == 0) return

      this.observeSlideIntersections()

      if(this.autoAdvanceTime) {
        this._poller = poll(() => this.goTo(this.currentPage + 1), this.autoAdvanceTime)
        this._poller.start()
      }
    },

    methods: {

      goTo(idx, immediate) {
        // Wrap idx into a valid value
        if(idx >= this.numPages) idx = 0
        else if(idx < 0) idx = this.numPages -1

        /**
         *  We can't use 'scrollIntoView' as this causes the parent page to jump around, especially problematic
         *  when we have 2 or more carousels on the same page, both with auto-advance turned on.
         */
        // this.$refs.scroller.children[idx].scrollIntoView({block: 'nearest', inline: 'center', behavior: this.scrollbehavior})

        // In peek mode, we apply padding as a percentage to the scroller, we need to offset for that
        const paddingOffset = this.peek ? this.$refs.scroller.offsetWidth * this.peekAmount : 0
        const targetPos = this.$refs.scroller.children[idx].offsetLeft - paddingOffset
        if(immediate)
          this.$refs.scroller.scrollLeft = targetPos
        else
          this.$refs.scroller.scrollTo({behavior: this.scrollbehavior, left: targetPos})

        /**
         * If IntersectionObserver is not supported on the client, we need to manually update the `currentPage` here
         */
        if(this._supportsIntersectionObserver == false) {
          this.currentPage = idx
        }
      },

      observeSlideIntersections() {

        // TODO:: IntersectionObserver needs to be recreated whenever numpages changes, or when elements in pages array changes

        if (this._supportsIntersectionObserver == false) return

        const pages = Array.from(this.$refs.scroller.children)

        const handleIntersect = entries => {
          const entry = entries.find(e => e.isIntersecting)
          if (entry) {
            this.currentPage = pages.indexOf(entry.target)
          }
        }

        // TODO:: threshold must properly calculated for "isReducedLastPage" scenarios
        this._observer = new IntersectionObserver(handleIntersect, {
          root: this.$refs.scroller,
          threshold: 0.95,
        })

        pages.forEach(el => this._observer.observe(el))
      },

      genScroller(h) {
        const columnTracks = this.computeColumnTracks()

        return h(
          'ul',
          {
            ref: 'scroller',
            class: 'carousel__page-scroller',
            style: `
              grid-auto-columns: ${columnTracks.map(track => `${track}%`).join(' ')};
              ${this.peek && this.centerPeakSlides ? `padding: 0 ${this.peekAmount * 100}%;` : ''}
            `,
          },
          this.genPages(h)
        )
      },

      genPages(h) {
        const pages = new Array(this.numPages).fill(null).map((_, idx) => {
          // Collect the raw slides that belongs to this page, wrapping each raw slide in a slide `carousel__slide`
          const start = idx * this.perPage
          const pageSlides = this.rawSlides.slice(start, start + this.perPage)
            .map(slideContent => h('article', {class: 'carousel__slide'}, [slideContent]))

          return h(
            'li',
            {
              class: 'carousel__page',
              on: {
                /*!capture*/'!click': (e) => {
                  // We only need to support "click to scroll to" functionality when in peek mode
                  if(this.peek == false) return

                  const targetPageIdx = Array.from(this.$refs.scroller.children).indexOf(e.currentTarget)
                  if(targetPageIdx != this.currentPage) {
                    e.preventDefault()
                    e.stopPropagation()

                    this.goTo(targetPageIdx)
                  }
                }
              }
            },
            pageSlides)
        })

        return pages
      },

      computeColumnTracks() {
        
        let columnTracks = this.isReducedLastPage
          ? [...new Array(this.numPages - 1).fill(100), 100/this.perPage * (this.numSlides % this.perPage)]
          : [100]

        if(this.peek && !this.centerPeakSlides) {
          columnTracks = columnTracks.map(track => track * (1 - this.peekAmount * 2))      
        }

        return columnTracks
      },

      genPageBreadcrumbs(h) {
        if(this.numPages == 1) return

        return h(
          'nav',
          {class: 'carousel__breadcrumbs'},
          new Array(this.numPages).fill().map((_, idx) => {
            return h(
              'n-button',
              {
                props: { flat: true, icon: true, },
                domProps: { tabIndex: -1, },
                on: {
                  click: (e) => this.goTo(idx),
                },
              },
              [h('n-icon', idx == this.currentPage ? ' filled_circle' : 'outline_circle')])
          })
        )
      },

      genPageControlBtn(h, direction) {
        if(this.numPages == 1) return

        return h(
          'n-button',
          {
            props: { flat: true, icon: false, secondary: true,small:true },
            domProps: { tabIndex: -1, },
            class: `carousel__page-control-btn carousel__page-control-btn--${direction}`,
            on: {
              click: (e) => this.goTo(this.currentPage + (direction == 'backward' ? -1: 1)),
            },
          },
          this.$slots[`control-btn-${direction}`] || [h('n-icon', {}, direction == 'backward' ? 'arrow_left': `arrow_right`)]
        )
      },

      pausePoller() {
        this._poller && this._poller.stop()
      },

      resumePoller() {
        this._poller && this._poller.start()
      },

      destroyPoller() {
        if(this._poller) {
          this._poller.stop()
          this._poller = null
        }
      },

    },

    beforeDestroy() {
      this._observer && this._observer.disconnect()
      this.destroyPoller()
    }

  }
</script>

<style lang="scss">
  .carousel {
    position: relative;
    display: flex;
    flex-direction: column;

    &__wrapper {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: center;

      flex: 1 1 auto;
    }

    &__page-scroller {
      position: relative;
      flex: 1 1 auto;
      display: grid;
      grid: 1fr / auto-flow 100%;
      height: 100%;
      gap: 1em;

      // Set up scroll container
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      overscroll-behavior-x: contain;
      touch-action: pan-x;
      // Hide scrollbar
      -ms-overflow-style: none;  /* Internet Explorer 10+ */
      scrollbar-width: none;  /* Firefox */
      &::-webkit-scrollbar {  display: none;  /* Safari and Chrome */ }

      /* reset <ul> */
      list-style: none; padding: 0; margin: 0;
    }

    &__page {
      display: flex;
      scroll-snap-align: center;
      gap: 1em;

      /* reset <li> */
      margin:0; padding: 0;
    }

    &__slide {
      position: relative;
      flex: 1 1 100%;

      > * { width: 100%; height: 100%; }
    }

    // makes sure a page or slide's content doesn't occupy more space than it should
    &__page,
    &__slide { overflow-x: hidden; }


    &__breadcrumbs {
      pointer-events: none;
      text-align: center;
      margin-top: 1em;

      > .btn {
        font-size: 0.75em;
        margin: 0 -0.1em;
        pointer-events: initial;
        opacity: 0.7;
      }
    }

    &--overlay-breadcrumbs {
      .carousel__breadcrumbs {
        position: absolute;
        z-index: 1;
        left: 0;
        right: 0;
        bottom: 0.1em;
      }
    }

    &--overlay-control-btns {
      .carousel__page-control-btn {
        background-color: black !important;
        border-radius: 0;
        padding-right: .5em;
        padding-left: .5em;
        position: absolute;
        z-index: 1;

        &--backward {
          left: 0;
          margin-left: 0;
        }
        &--forward {
          right: 0;
          margin-right: 0;
        }
      }
    }

  }
</style>
